import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LANGS } from "@/i18n/content";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "lessons", label: t.nav.lessons },
    { id: "schedule", label: t.nav.schedule },
    { id: "gallery", label: t.nav.gallery },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-cream/70 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        {/* Brand */}
        <button
          data-testid="brand-logo"
          onClick={() => scrollTo("hero")}
          className={`font-playfair text-xl md:text-2xl tracking-tight transition-colors ${
            scrolled ? "text-ink" : "text-white"
          }`}
        >
          Atlas Ziz <span className="italic text-sand">Tours</span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className={`label-mono transition-colors hover:text-ocean ${
                scrolled ? "text-ink/70" : "text-white/80"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          {/* Language switcher */}
          <div
            data-testid="language-switcher"
            className={`flex items-center gap-1 border ${
              scrolled ? "border-black/15" : "border-white/30"
            }`}
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-2.5 py-1 label-mono transition-colors ${
                  lang === l.code
                    ? "bg-ocean text-white"
                    : scrolled
                    ? "text-ink/70 hover:text-ocean"
                    : "text-white/80 hover:text-white"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <button
            data-testid="header-book-btn"
            onClick={() => scrollTo("booking")}
            className="hidden md:inline-flex bg-navy text-white label-mono px-5 py-3 hover:bg-ocean transition-colors duration-300"
          >
            {t.nav.bookShort}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden ${scrolled ? "text-ink" : "text-white"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-cream border-t border-black/5">
          <nav className="flex flex-col px-5 py-4">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => {
                  scrollTo(l.id);
                  setOpen(false);
                }}
                className="text-left py-3 font-outfit text-lg text-ink border-b border-black/5"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => {
                scrollTo("booking");
                setOpen(false);
              }}
              className="mt-4 bg-navy text-white label-mono py-4"
            >
              {t.nav.book}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
